import type { Blueprint, Kitchen } from '@rta/rta-blueprint-library';

import {
	BACKSPLASH_BODY_STYLE,
	BACKSPLASH_EMPTY_STYLE,
	BACKSPLASH_FOOTER_STYLE
} from './blueprint-backsplash-page.styles';
import type { BlueprintPage } from '../types';
import { BlueprintPageFooter } from '../shared/blueprint-page-footer';
import { BlueprintPageHeader } from '../shared/blueprint-page-header';
import { BlueprintPageWrapper } from '../shared/blueprint-page-wrapper';
import { BlueprintPanelFooter } from '../shared/blueprint-panel-footer';
import { DisplayMode } from '@r-core';
import { EditableText } from '../shared/editable-text';
import { EmbeddedSvgViewport } from '../shared/embedded-svg-viewport';
import { GRANITE_FINISH } from '../helpers/get-countertop-label';
import { getPageNoteScope } from '../helpers/get-page-note-scope';
import { getPageOverrideScope } from '../helpers/get-page-override-scope';

export type BlueprintBacksplashPageProps = {
	page: BlueprintPage;
	blueprint: Blueprint;
	kitchen: Kitchen;
	customerName?: string;
	layoutDescription?: string;
	designerName?: string;
	creationDate?: string;
	threeDImageUrl?: string;
};

export const BlueprintBacksplashPage = (props: BlueprintBacksplashPageProps): React.ReactElement => {
	const {
		page,
		blueprint,
		kitchen,
		customerName,
		layoutDescription,
		designerName,
		creationDate,
		threeDImageUrl
	} = props;

	// the page simply embeds the real backsplash viewport, so it always matches the designer's Backsplash view
	const firstIslandId = kitchen.design.islands.children[0]?.id ?? '';
	const hasBacksplashes = kitchen.design.islands.children
		.some((island) => (island.getCountertop()?.backsplashes?.children ?? []).length > 0);

	// Backsplashes are cut from the same slab as the countertop, so a granite kitchen needs the same
	// material label the countertop and joint-countertop sheets lead with (GitHub #417).
	const notes = hasBacksplashes && kitchen.material?.countertop?.isGranite
		? [`Granite | ${GRANITE_FINISH}`]
		: [];

	return (
		<BlueprintPageWrapper pageNumber={page.pageNumber}>
			<BlueprintPageHeader
				title={page.title}
				pageNumber={page.pageNumber}
				customerName={customerName}
				layoutDescription={layoutDescription}
				designerName={designerName}
				creationDate={creationDate}
				threeDImageUrl={threeDImageUrl}
				showThumbnail={true}
			/>

			<div style={{ ...BACKSPLASH_BODY_STYLE, clipPath: 'inset(0)' }}>
				{hasBacksplashes ? (
					<EmbeddedSvgViewport
						blueprint={blueprint}
						islandId={firstIslandId}
						panelId={null}
						displayMode={DisplayMode.BacksplashManufacturing}
						pageIndex={page.pageNumber}
						pageType={page.pageType}
					/>
				) : (
					<EditableText overrideKey='backsplash.empty' style={BACKSPLASH_EMPTY_STYLE}>
						BACKSPLASHES
					</EditableText>
				)}
			</div>

			{/* Combined panel footer + page footer */}
			<div style={BACKSPLASH_FOOTER_STYLE}>
				<BlueprintPanelFooter scope={getPageOverrideScope(page)} notes={notes} noteScope={getPageNoteScope(page)} />
				<BlueprintPageFooter pageNumber={page.pageNumber} />
			</div>
		</BlueprintPageWrapper>
	);
};
